import React from 'react';
import { BuyerInventoryActivityReportVO } from "../../../types/model/valueObject/inventoryActivity/BuyerInventoryActivityReportVO";
import { useSnackbar } from "../../../context/snackbar-context";
import {
  CollapsableCardBodyContainer,
  CollapsableCardClickableContainer,
  CollapsableCardCollapseContainer,
  CollapsableCardContainer,
  CollapsableCardExpandIconButton,
  CollapsableCardHeaderActionsContainer,
  CollapsableCardHeaderContainer,
  CollapsableCardHeaderItem,
  CollapsableCardBodyItem,
  CollapsableCardBodyActionsContainer,
  ActionsMenuOpenButton,
  ActionsMenu,
  ActionItem,
  DialogActionItem
} from "../../cardComponents";
import { useMenu } from "../../../hooks/report/useMenu";
import {
  ActionTypes,
  ActionDefaultIcons,
  ActionPayload,
  CallbackActions,
  type OnActionCallback,
} from "../../../util/ActionUtils";
import BuyerInterchangeImageModal from "./BuyerInterchangeImageModal";
import { 
  InterchangeActivityType
} from '../../constants/securspace-constants';

export interface BuyerInterchangeCardListItemProps {
  item: BuyerInventoryActivityReportVO;
  expanded?: boolean;
  onToggleExpand: () => void;
}

const openTabActionPayloads = {
  viewInterchange: {
    endpoint: (item: BuyerInventoryActivityReportVO) => `/api/interchanges/${item.id}`,
    errorMessage: "Unable to view this interchange",
  },
} satisfies Record<string, ActionPayload<BuyerInventoryActivityReportVO>>;

/**
 * Component that displays all information for a single buyer invoice item
 * @param item the InvoiceTransactionVO to display
 * @param expanded
 * @param onToggleExpand
 * @constructor
 */

const BuyerInterchangeCardListItem = ({
  item,
  expanded,
  onToggleExpand
}: BuyerInterchangeCardListItemProps) => {

  const { anchorEl, isOpen, handleClick, handleClose } = useMenu();
  const { snackbarShowMessage } = useSnackbar();
  const isCheckOut = item?.activityType === InterchangeActivityType.CHECK_OUT;

  const actionCallback: OnActionCallback = (action, _instrument, message) => {
    if (action === CallbackActions.ERROR && message) {
      snackbarShowMessage(message, 'error');
    }
    handleClose();
  };

  return (
    <CollapsableCardContainer expanded={expanded}>
      <CollapsableCardClickableContainer onClick={onToggleExpand}>
        <CollapsableCardHeaderContainer>
          <CollapsableCardHeaderItem value={item?.activity} label={'Activity'} />
          <CollapsableCardHeaderItem value={item?.activityDate} label={'Date'} />
          <CollapsableCardHeaderItem value={item?.containerNumber} label={'Container'} />
          <CollapsableCardHeaderItem value={item?.trailerNumber} label={'Trailer'} />
          <CollapsableCardHeaderItem value={item?.chassisNumber} label={'Chassis'} />
          <CollapsableCardHeaderItem value={item?.truckLicensePlateNumber} label={'Truck License Plate Number'} />
          <CollapsableCardHeaderActionsContainer>
            <CollapsableCardExpandIconButton expanded={expanded ?? false} />
          </CollapsableCardHeaderActionsContainer>  
        </CollapsableCardHeaderContainer>
      </CollapsableCardClickableContainer>
      <CollapsableCardCollapseContainer in={expanded}>
        <CollapsableCardBodyContainer>
          <CollapsableCardBodyItem value={item?.chassisLicensePlateNumber} label={'Chassis License #'} />
          <CollapsableCardBodyItem value={item?.assetSize} label={'Size'} />
          <CollapsableCardBodyItem value={item?.driverFirstName} label={'First Name (Driver)'} />
          <CollapsableCardBodyItem value={item?.driverLastName} label={'Last Name (Driver)'} />
          <CollapsableCardBodyItem value={item?.driverLicenseNumber} label={'License (Driver)'} />
          <CollapsableCardBodyItem value={item?.sealNumber} label={'Seal'} />

          <CollapsableCardBodyItem value={item?.assetType} label={'Type'} />
          <CollapsableCardBodyItem value={item?.orderNumber} label={'Booking'} />
          <CollapsableCardBodyItem value={item?.locationName} label={'Location'} />
          <CollapsableCardBodyItem value={item?.supplierName} label={'Supplier'} />
          <CollapsableCardBodyItem value={item?.currentInventory} label={'Inventory'} />
          <CollapsableCardBodyItem value={item?.correctionStatus} label={'Correction Status'} />

          <CollapsableCardBodyItem value={item?.notes} label={'Notes'} />
          <CollapsableCardBodyItem value={item?.scacCode} label={'SCAC Code'} />
          <CollapsableCardBodyItem value={item?.dwellTime} label={'Dwell Time'} show={isCheckOut} />
          <CollapsableCardBodyItem value={item?.dwellTimeInMinutes} label={'Dwell Time Minutes'} show={isCheckOut} />

          <CollapsableCardBodyActionsContainer>
            <ActionsMenuOpenButton onClick={handleClick}/>
            <ActionsMenu open={isOpen} anchorEl={anchorEl} onClose={handleClose}>
              <ActionItem
                label="Print"
                icon={ActionDefaultIcons[ActionTypes.DOWNLOAD]}
                enable={Boolean(item?.activityDate)}
                type={ActionTypes.OPEN_TAB}
                instrument={item}
                actionPayload={openTabActionPayloads.viewInterchange}
                onAction={actionCallback}
              />
              <DialogActionItem
                label="View Images"
                icon={ActionDefaultIcons[ActionTypes.DIALOG]}
                enable={Boolean(item?.inventoryGallery && item.inventoryGallery.length > 0)}
                show={Boolean(item?.inventoryGallery && item.inventoryGallery.length > 0)}
                dialog={BuyerInterchangeImageModal}
                dialogProps={{ item }}
                onDialogClose={handleClose}
              />
            </ActionsMenu>
          </CollapsableCardBodyActionsContainer>

        </CollapsableCardBodyContainer>
      </CollapsableCardCollapseContainer>
    </CollapsableCardContainer>
  );
};

export { BuyerInterchangeCardListItem };
